/**
 * Clause display helpers — issue #821 / plan 43.
 *
 * Backend stores `section_heading = NULL` for marker-only clauses (no real
 * heading, just a structural marker like "1." or "A."). These helpers
 * centralise the UI fallback rules so titles are never blank and so the
 * card body can strip a leading marker when the title is synthetic.
 */

/**
 * Returns a non-blank display title for a clause.
 *
 * Preference order:
 *   1. Real `section_heading` from extraction.
 *   2. `Clause {logical_id}` if `logical_id` present.
 *   3. `Clause {fallbackIndex + 1}` if `fallbackIndex` provided.
 *   4. `Untitled clause` final guard.
 */
export function getClauseDisplayTitle(
  sectionHeading: string | null | undefined,
  logicalId: string | null | undefined,
  fallbackIndex?: number,
): string {
  const h = (sectionHeading || '').trim();
  if (h) return h;
  if (logicalId) return `Clause ${logicalId}`;
  if (typeof fallbackIndex === 'number') return `Clause ${fallbackIndex + 1}`;
  return 'Untitled clause';
}

// Numbered ("1.", "1.1", "10.1.2"), single-uppercase-letter ("A."), or
// parenthetical lowercase ("(a)", "(iv)") markers. Trailing whitespace is
// included in the match so callers can slice the prefix off directly.
const LEADING_MARKER_RE = /^(?:\d+(?:\.\d+)*\.?|[A-Z]\.|\([a-z]+\))[\s.]+/;

/**
 * Returns the leading structural marker prefix of `text` (including trailing
 * punctuation/whitespace) or `null` if no marker is present.
 */
export function getLeadingMarkerPrefix(text: string): string | null {
  const m = text.match(LEADING_MARKER_RE);
  return m ? m[0] : null;
}

/**
 * Returns `text` with any leading structural marker prefix removed.
 */
export function stripLeadingMarker(text: string): string {
  const prefix = getLeadingMarkerPrefix(text);
  return prefix ? text.slice(prefix.length) : text;
}

/**
 * Removes `marker` from the front of `title`, but only on an EXACT match.
 *
 * Plan 236 Phase C / #974. Production headings already print the number
 * ("4. LIABILITY CAP"), so a card that also shows the marker chip would print
 * it twice. The strip is exact rather than generic for one specific reason:
 * `stripLeadingMarker` above matches any leading marker shape, so on a title
 * of "4.20 Days" it happily removes "4.2" and leaves "0 Days". Matching the
 * persisted marker literally, with a required boundary after it, cannot.
 *
 * Returns `title` unchanged when the marker is absent, when the boundary rule
 * is not met, or when stripping would leave nothing — a blank heading is worse
 * than a repeated number.
 */
export function stripExactMarker(
  title: string,
  marker: string | null | undefined,
): string {
  if (!title || !marker) return title;
  // Escape for use inside a RegExp: markers legitimately contain "." and "()".
  const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Optional trailing punctuation, then REQUIRED whitespace. Without the
  // whitespace requirement "4.2" would match inside "4.20".
  const match = title.match(new RegExp(`^${escaped}[.):]?[\\s\\u00A0]+`));
  if (!match) return title;
  const rest = title.slice(match[0].length).trim();
  return rest || title;
}
